import type {
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  Ref,
} from 'react';
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
// @todo (SRCH-2005): Remove motion import
import { motion } from 'framer-motion';
import styled from 'styled-components';
import type {
  Key,
  Selection,
  SelectionBehavior,
  SelectionMode,
} from '@react-types/shared';
import type { QuickSearchResult } from '@frameio/graphql-client/generated';
import { useActiveProjectId } from '@frameio/shared/contexts';
import type { ListLoadingStates, ListProps } from '@frameio/vapor';
import {
  ListBox,
  ListSearch,
  mergeProps,
  theme,
  useFilteredListState,
  usePrevious,
} from '@frameio/vapor';
import { useIsAdvancedSearchEnabled } from '../../../advanced-search/hooks';
import { useInputSelectionOnFocus } from '../../../hooks/useInputSelectionOnFocus';
import { useQuickSearchModal } from '../../../shared/contexts';
import {
  useTrackProjectScopeSelected,
  useTrackSearchQueryCleared,
} from '../../../shared/contexts/SearchAnalyticsContext';
import type { ListMode } from '../../hooks/useQuickSearchListMode';
import type {
  FilterKeys,
  OnSearchSuggestionClick,
  QuickSearchFilters,
  QuickSearchResults,
  QuickSearchResultSection,
} from '../../types';
import { getActiveListItemDetails } from '../../utils';
import { QuickSearchFilter } from '../QuickSearchFilter';
import { QuickSearchInitial } from '../QuickSearchInitial';
import { QuickSearchItemDetail } from '../QuickSearchItemDetail';
import { QuickSearchNlpResultsFooter } from '../QuickSearchTabBar';
import {
  DetailSkeleton,
  ListSkeleton,
  LoadingMoreSkeleton,
  ProjectScopeDropdown,
  QuickSearchListAnimation,
  QuickSearchResultsFooter,
  ResultsMessage,
} from './components';
import TEXT from './generated/TEXT/QuickSearchList';
import {
  useHasPaneScrolled,
  usePlaceholderText,
  useSyncedQuickSearchSelection,
} from './hooks';
import { useQuickSearchScrollManager } from './hooks/useQuickSearchScrollManager';

TEXT.DEFINE({
  /** Label for the back/close button to exit the search modal */
  close: 'Quit Search',
  /** Loading indicator label shown while search is in progress */
  pendingStateLabel: 'Searching',
  /** Accessible label for the button that clears the search input */
  clearButtonAriaLabel: 'Clear search input',
});

/**
 * EaseInOutQuint easing function - matches Vapor's internal default for smooth scrolling.
 * @see useSmoothScrollTo in @frameio/vapor
 */
const easeInOutQuint = (t: number): number => {
  if (t < 0.5) {
    return 16 * t * t * t * t * t;
  }
  const u = t - 1;
  return 1 + 16 * u * u * u * u * u;
};

const INITIAL_PANE_ID = 'initial';
const LIST_PANE_ID = 'item-list';
const DETAIL_PANE_ID = 'item-detail';

const MODAL_MAX_WIDTH = 1036;
const MODAL_CONTENT_HEIGHT = 580;
const CELL_RESULT_HEIGHT = 106;
const CELL_RESULT_HEIGHT_MOBILE = 88;
const DIALOG_GUTTER_WITH_BORDER = 18;
const LIST_RESULTS_PADDING_BOTTOM = 24;

/** Max width of the results list column in the content grid. */
const CONTENT_LIST_MAX_WIDTH = 714;
/** Fixed width of the detail pane in the content grid. */
const CONTENT_DETAIL_PANE_WIDTH = 320;

const DefaultListProps = {
  disallowEmptySelection: true,
  itemGap: 0,
};

const ListPropsMobile: {
  selectionMode: SelectionMode;
  estimatedItemHeight: number;
} = {
  selectionMode: 'none',
  estimatedItemHeight: CELL_RESULT_HEIGHT_MOBILE,
};

const ListPropsDesktop: {
  selectionMode: SelectionMode;
  selectionBehavior: SelectionBehavior;
  estimatedItemHeight: number;
} = {
  selectionMode: 'single',
  selectionBehavior: 'replace',
  estimatedItemHeight: CELL_RESULT_HEIGHT,
};

/**
 * Type guard to check if items is an array of sections (MULTI mode)
 */
const isItemsSections = (
  items: QuickSearchResults
): items is QuickSearchResultSection[] => {
  return items.length > 0 && 'items' in items[0];
};

/**
 * helper function with a fixed reference to render a skeleton for the list
 * this is necessary to avoid re-rendering the skeleton when the list state changes
 */
function renderLoadingSkeleton(
  loadingStatus: ListLoadingStates,
  listMode: ListMode
) {
  if (loadingStatus === 'loading-more') {
    return <LoadingMoreSkeleton />;
  }
  return <ListSkeleton mode={listMode} />;
}

function renderPlaceholderSkeleton(listMode: ListMode) {
  return <ListSkeleton mode={listMode} />;
}

const shouldEmitProgrammaticSelectionChange = (
  listKey: Key | null | undefined,
  focusedKey?: string | null
): listKey is Key =>
  listKey !== undefined &&
  listKey !== null &&
  String(listKey) !== String(focusedKey ?? '');

type QuickSearchListProps = Omit<ListProps<QuickSearchResult>, 'items'> & {
  filters: QuickSearchFilters;
  onToggleFilter: (filter: FilterKeys, value: boolean) => void;
  onClearAllFilters: () => void;
  /**
   * Callback to request the close of the Quick Search modal
   */
  onRequestClose: () => void;
  /**
   * Callback to reset the query state
   */
  onResetQueryState: () => void;
  /**
   * Whether the Quick Search list should show certain layout elements
   */
  isMobile: boolean;
  items: QuickSearchResults;
  listMode: ListMode;
  /**
   * The actual search query (debounced) used for GraphQL queries
   */
  searchQuery: string;
  /**
   * Callback to notify parent when input query is stale (during debounce)
   */
  onInputQueryStaleChange?: (isStale: boolean) => void;
  /**
   * Whether the semantic search feature is enabled
   */
  isSemanticSearchEnabled: boolean;
  /**
   * Callback invoked when a result is activated via keyboard.
   * Receives the focused list item key (e.g., "AssetResult:123:0" or "lexical:AssetResult:123:0")
   */
  onActivateItem?: (focusedKey: string) => void;
  /**
   * Whether the modal is initial render
   */
  isInitial?: boolean;
  /** The key of the item in list */
  focusedKey: string | null;
  /** Optional tab bar rendered between search input and filter bar */
  tabBar?: ReactNode;
  /** Overrides the default "No Results" message. Only shown when there is no error. */
  noResultsContent?: ReactNode;
  /** When true, renders the NLP results footer with info text and item actions */
  showNlpResultsFooter?: boolean;
  /** Optional ref to the rendered results listbox for focus transfers */
  resultsListRef?: Ref<HTMLDivElement>;
  /** Remounts the rendered results list when the backing result cycle changes. */
  resultsListResetKey?: string;
};

/**
 * This component is responsible for the presentation and lifecycle of the quick search list.
 * It handles navigation to a selected result, and adapts its layout for various viewports,
 * and also handles the various results states of the list .
 */
export const QuickSearchList = forwardRef<
  HTMLInputElement,
  QuickSearchListProps
>(function QuickSearchListComponent(
  {
    'aria-label': ariaLabelProp,
    children,
    filters,
    focusedKey,
    isInitial = false,
    isMobile,
    isSemanticSearchEnabled,
    items: itemsProp,
    listMode,
    loading: loadingProp,
    onActivateItem,
    onClearAllFilters,
    onInputQueryStaleChange,
    onRequestClose,
    onRequestSearch,
    onResetQueryState,
    onToggleFilter,
    searchQuery,
    tabBar,
    noResultsContent,
    showNlpResultsFooter,
    resultsListRef,
    resultsListResetKey,
    onSelectionChange,
    ...otherProps
  }: QuickSearchListProps,
  forwardedRef
) {
  // Local ref required because forwardedRef may be a callback ref.
  // useInputSelectionOnFocus needs a stable RefObject for event listeners.
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(forwardedRef, () => inputRef.current!, []);

  const trackSearchQueryCleared = useTrackSearchQueryCleared();
  const trackProjectScopeSelected = useTrackProjectScopeSelected();

  const { scheduleSelection } = useInputSelectionOnFocus(inputRef);

  const placeholderText = usePlaceholderText({
    isMobile,
    isSemanticSearchEnabled,
  });

  const { clearScopedToProjectId, setScopedToProjectId, scopedToProjectId } =
    useQuickSearchModal();
  const activeProjectId = useActiveProjectId();
  const isScopedToProject = Boolean(scopedToProjectId);
  const dropdownRef = useRef<HTMLButtonElement>(null);

  const listProps = mergeProps(
    {
      ...DefaultListProps,
      ...(isMobile ? ListPropsMobile : ListPropsDesktop),
      onRequestSearch,
      loading: loadingProp,
      items: itemsProp,
      onSelectionChange,
      'aria-label': ariaLabelProp,
      children,
      defaultQuery: isInitial ? searchQuery.trim() : undefined,
    },
    otherProps
  );

  const { state: listState } = useFilteredListState(
    listProps as ListProps<QuickSearchResult>
  );

  const hasSearchQuery = listState.queryState.query.trim() !== '';

  // Detect if input query is ahead of the actual search query (during debounce period)
  // searchQuery is always trimmed (see handleRequestSearch), so we trim the queryState for an accurate comparison
  const isInputQueryStale =
    listState.queryState.query.trim() !== '' &&
    listState.queryState.query.trim() !== searchQuery;

  // Check if we're in split list mode (MULTI mode with sections)
  const isSplitListMode = isItemsSections(itemsProp);

  const isTabbedListMode = listMode === 'MULTI_TABBED';
  const listPaddingY = 0;
  const searchFieldState = useMemo(
    () => (isTabbedListMode ? { ...listState, isAsync: false } : listState),
    [isTabbedListMode, listState]
  );
  // In MULTI mode, paddingBottom sits between lexical items and the loading-more
  // skeleton divider. Keeping it at 10 (matching main) prevents the abnormal gap
  // that appears when standard results load but NLP is still in-flight.
  const listPaddingBottom = isSplitListMode ? 10 : LIST_RESULTS_PADDING_BOTTOM;
  const shouldShowTabBar = Boolean(tabBar);
  const shouldShowFilterMenu = hasSearchQuery;
  const shouldShowSearchControls =
    hasSearchQuery && (shouldShowTabBar || shouldShowFilterMenu);

  const focusInput = useCallback(() => {
    if (forwardedRef && 'current' in forwardedRef) {
      forwardedRef.current?.focus();
    }
  }, [forwardedRef]);

  const handleSearchInputKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        // Prevent native <input type="search"> from clearing the input value
        // so the query persists when the dialog closes via ESC.
        // The event still propagates to the Dialog which handles modal closing.
        event.preventDefault();
        return;
      }

      if (dropdownRef.current) {
        const input = event.currentTarget;
        const isEmpty = input.value === '';
        // Safari selects all text on input focus, leaving selectionEnd > 0 even
        // when the cursor is at the start. Checking only selectionStart === 0
        // ensures one ArrowLeft press focuses the dropdown in all browsers.
        const cursorAtStart = input.selectionStart === 0;

        // Focus pill when Left Arrow at start of input
        if (event.key === 'ArrowLeft' && (isEmpty || cursorAtStart)) {
          event.preventDefault();
          dropdownRef.current.focus();
          return;
        }
      }

      // Activate focused list item when Enter key is pressed
      if (event.key === 'Enter' && !event.repeat && onActivateItem) {
        event.preventDefault();
        onActivateItem(String(focusedKey));
      }
    },
    [onActivateItem, focusedKey]
  );

  const selectListItemFromListKey = useCallback(
    (listKey?: string | number | null) => {
      if (!listKey) return;
      listState.selectionManager.setFocusedKey(listKey);
      listState.selectionManager.replaceSelection(listKey);
      // Keep external focusedKey (used by detail pane) in sync with
      // programmatic selection changes (tab switch / auto-select), while
      // avoiding no-op selection updates.
      if (shouldEmitProgrammaticSelectionChange(listKey, focusedKey)) {
        onSelectionChange?.(new Set([listKey]) as Selection);
      }
    },
    [listState.selectionManager, focusedKey, onSelectionChange]
  );

  useSyncedQuickSearchSelection({
    collection: listState.collection,
    focusedKey,
    isSectionedList: isSplitListMode,
    items: itemsProp,
    selectListItemFromListKey,
  });

  const hasSetInitialSelectedItem = useRef(false);
  const shouldSelectAllOnOpen = useRef<boolean | null>(null);
  const hasAppliedSelection = useRef(false);
  const prevIsInitial = usePrevious(isInitial);

  const handleOnClickSearchSuggestion = useCallback<OnSearchSuggestionClick>(
    (value, highlight) => {
      listState.dispatchQuery({ type: 'query', state: value });
      focusInput();
      if (highlight) {
        hasAppliedSelection.current = true;
        scheduleSelection(highlight);
      }
    },
    [listState, focusInput, scheduleSelection]
  );

  const handleOnClearAll = useCallback(() => {
    onClearAllFilters();
    onResetQueryState();
    listState.dispatchQuery({ type: 'clear-query' });
    focusInput();
  }, [onClearAllFilters, onResetQueryState, listState, focusInput]);

  const handleOnQueryClear = useCallback(() => {
    // Collapse selection to prevent flash when focus returns to input
    inputRef.current?.setSelectionRange(0, 0);
    onClearAllFilters();
    trackSearchQueryCleared();
  }, [trackSearchQueryCleared, onClearAllFilters]);

  const handleOnProjectPillClear = useCallback(() => {
    if (!scopedToProjectId) {
      focusInput();
      return;
    }

    clearScopedToProjectId();
    trackProjectScopeSelected('AllProjects');
    focusInput();
  }, [
    clearScopedToProjectId,
    focusInput,
    scopedToProjectId,
    trackProjectScopeSelected,
  ]);

  const handleOnSetProjectScope = useCallback(
    (projectId: string) => {
      if (!projectId) return;

      if (scopedToProjectId !== projectId) {
        trackProjectScopeSelected('CurrentProject');
      }
      setScopedToProjectId(projectId);
      focusInput();
    },
    [
      focusInput,
      scopedToProjectId,
      setScopedToProjectId,
      trackProjectScopeSelected,
    ]
  );

  // Notify parent when stale state changes
  useLayoutEffect(() => {
    onInputQueryStaleChange?.(isInputQueryStale);
  }, [isInputQueryStale, onInputQueryStaleChange]);

  /**
   * Show loading skeleton when parent indicates loading state.
   * Note: isInputQueryStale is handled separately by rendering skeleton directly.
   */
  const isLoading =
    loadingProp === 'loading' ||
    (loadingProp === undefined &&
      listState.isLoadingVisible &&
      listState.collection.size === 0);

  // Check if the collection size has settled
  const previousCollectionSize = usePrevious(listState.collection.size);
  const hasCollectionSettled =
    listMode === 'SINGLE'
      ? true
      : previousCollectionSize === listState.collection.size;

  /**
   * Get the first list item key for the list.
   * In multi mode (when items are sections), we skip the first item because it is the section.
   */
  const getFirstListItemKey = useCallback(() => {
    let firstKey = listState.collection.getFirstKey();

    if (!firstKey) return null;

    if (isItemsSections(itemsProp)) {
      firstKey = listState.collection.getKeyAfter(firstKey);
    }
    return firstKey;
  }, [itemsProp, listState.collection]);

  const { handleOnScroll, hasAnyPaneScrolled, resetPaneScrollState } =
    useHasPaneScrolled({
      paneIds: [LIST_PANE_ID, DETAIL_PANE_ID, INITIAL_PANE_ID],
    });

  const { handleOnListScroll, handleOnDetailScroll, handleOnInitialScroll } =
    useQuickSearchScrollManager({
      listState,
      hasCollectionSettled,
      handleOnScroll,
      hasAnyPaneScrolled,
      resetPaneScrollState,
      selectListItemFromListKey,
      getFirstListItemKey,
      focusInput,
      paneIds: {
        list: LIST_PANE_ID,
        detail: DETAIL_PANE_ID,
        initial: INITIAL_PANE_ID,
      },
      isInitial,
      filters, // @todo (SRCH-2005): Remove (filter-change auto-select reset)
    });

  // If prevLoadingVisible is true, wait until the current isLoadingVisible has settled to false
  // If prevLoadingVisible is already false, wait until the current isLoadingVisible is also false
  const hasLoadingStateSettled = listState.prevLoadingVisible
    ? listState.prevLoadingVisible !== listState.isLoadingVisible
    : listState.prevLoadingVisible === listState.isLoadingVisible;
  const hasLoadingVisibleSettled =
    listMode === 'SINGLE' ? true : hasLoadingStateSettled;

  const noResults =
    loadingProp === undefined &&
    hasSearchQuery &&
    !listState.isLoadingVisible &&
    listState.collection.size === 0 &&
    hasLoadingVisibleSettled &&
    hasCollectionSettled;

  const hasLoadingError = loadingProp === 'loading-error';

  // Derive details about the active list item from the selection manager for the detail pane
  const { isItemSelected } = getActiveListItemDetails(
    focusedKey ? String(focusedKey) : null
  );

  /**
   * Set initial list item selection for keyboard navigation.
   * Runs once per modal open when results are available.
   */
  useLayoutEffect(() => {
    if (
      isInitial &&
      !hasSetInitialSelectedItem.current &&
      listState.collection.size > 0 &&
      hasSearchQuery
    ) {
      selectListItemFromListKey(focusedKey);
      hasSetInitialSelectedItem.current = true;
    }
  }, [
    isInitial,
    listState.collection.size,
    hasSearchQuery,
    selectListItemFromListKey,
    focusedKey,
  ]);

  /**
   * Select-all when modal reopens with a saved query.
   * Partial selection (from suggestion clicks) takes precedence.
   */
  useLayoutEffect(() => {
    const isFirstMount = shouldSelectAllOnOpen.current === null;
    const isModalReopen = isInitial && prevIsInitial === false;

    if (isFirstMount || isModalReopen) {
      shouldSelectAllOnOpen.current = searchQuery.trim() !== '';
      hasAppliedSelection.current = false;
    }

    if (
      isInitial &&
      hasSearchQuery &&
      shouldSelectAllOnOpen.current &&
      !hasAppliedSelection.current
    ) {
      hasAppliedSelection.current = true;
      scheduleSelection({ starts: 0, ends: Infinity });
    }
  }, [
    isInitial,
    prevIsInitial,
    hasSearchQuery,
    searchQuery,
    scheduleSelection,
  ]);

  const scrollToKey = useMemo(() => {
    return isInitial && focusedKey
      ? {
          id: focusedKey as Key,
          scroll: 'smooth' as const,
        }
      : undefined;
  }, [focusedKey, isInitial]);

  /**
   * Configure scroll behavior for initial mount with preserved selection.
   * Uses 'center' alignment to scroll the preserved item to the middle of the viewport.
   */
  const getAutoScrollConfig = useCallback(
    (key: Key) => {
      // Only center on initial mount with preserved selection
      if (isInitial && key === focusedKey) {
        return {
          alignment: 'center' as const,
          behavior: {
            type: 'smooth' as const,
            ease: easeInOutQuint,
          },
        };
      }
      // Default behavior for regular focus changes
      return {
        alignment: 'auto' as const,
        behavior: { type: 'immediate' as const },
      };
    },
    [isInitial, focusedKey]
  );

  // Derive mutually exclusive content states for cleaner rendering.
  // showSkeleton includes both debounce period (isInputQueryStale) AND initial loading
  // to prevent blink when switching from forced skeleton to ListBox's skeleton.
  const showNoResultsMessage = noResults || hasLoadingError;
  const showSkeleton =
    !showNoResultsMessage && (isInputQueryStale || isLoading);
  const showResults = !showNoResultsMessage && !isInputQueryStale && !isLoading;

  const shouldShowProjectPill = activeProjectId != null;

  const isAdvancedSearchEnabled = useIsAdvancedSearchEnabled();
  const shouldShowSearchFooter = isAdvancedSearchEnabled;

  const searchInput = (
    <ListSearch
      ref={inputRef}
      adornmentBefore={
        shouldShowProjectPill && (
          <ProjectScopeDropdown
            ref={dropdownRef}
            activeProjectId={activeProjectId as string}
            onClear={handleOnProjectPillClear}
            onFocusInput={focusInput}
            onSetProjectScope={handleOnSetProjectScope}
            scopedToProjectId={scopedToProjectId}
          />
        )
      }
      clearButtonAriaLabel={TEXT.clearButtonAriaLabel}
      data-testid="quick-search-v2-input"
      gapY="0"
      iconVariant={isSemanticSearchEnabled ? 'sparkle' : 'default'}
      navigateBackLabel={TEXT.close}
      onKeyDown={handleSearchInputKeyDown}
      onNavigateBack={isMobile ? onRequestClose : undefined}
      onQueryClear={handleOnQueryClear}
      paddingBlockStart={0}
      paddingX={0}
      pendingStateLabel={TEXT.pendingStateLabel}
      placeholder={placeholderText}
      separator={null}
      size="large"
      state={searchFieldState}
      variant="pill"
    />
  );

  const resultsContent = hasSearchQuery ? (
    <SearchResultsContainer>
      {showNoResultsMessage && (
        <NoResultsContainer>
          {!hasLoadingError && noResultsContent ? (
            noResultsContent
          ) : (
            <ResultsMessage
              isError={hasLoadingError}
              onPress={handleOnClearAll}
            />
          )}
        </NoResultsContainer>
      )}
      {showSkeleton && (
        <ContentGrid>
          <ResultItemsContainer>
            <ListSkeleton mode={listMode} />
          </ResultItemsContainer>
          <ResultItemDetailContainer>
            <DetailSkeleton />
          </ResultItemDetailContainer>
        </ContentGrid>
      )}
      {showResults && (
        <ContentGrid>
          <ResultItemsContainer>
            <ListBoxScrollArea>
              <ListBox
                key={resultsListResetKey}
                {...listProps}
                ref={resultsListRef}
                data-testid="quick-search-v2-results"
                getAutoScrollConfig={getAutoScrollConfig}
                id={listState.id}
                loading={loadingProp}
                onScroll={handleOnListScroll}
                paddingBottom={listPaddingBottom}
                paddingY={listPaddingY}
                renderLoading={(loadingStatus) =>
                  renderLoadingSkeleton(loadingStatus, listMode)
                }
                renderPlaceholder={() => renderPlaceholderSkeleton(listMode)}
                scrollToKey={scrollToKey}
                state={listState}
                variant="unstyled"
              />
            </ListBoxScrollArea>
            {isTabbedListMode && shouldShowSearchFooter ? (
              <TabbedResultsFooterRow>
                {shouldShowSearchFooter && (
                  <QuickSearchResultsFooter inline={true} />
                )}
                {showNlpResultsFooter && shouldShowSearchFooter && (
                  <TabbedResultsFooterDivider aria-hidden={true} />
                )}
                {showNlpResultsFooter && (
                  <QuickSearchNlpResultsFooter inline={true} />
                )}
              </TabbedResultsFooterRow>
            ) : (
              <>
                {showNlpResultsFooter && <QuickSearchNlpResultsFooter />}
                {shouldShowSearchFooter && <QuickSearchResultsFooter />}
              </>
            )}
          </ResultItemsContainer>
          <ResultItemDetailContainer>
            {isLoading && <DetailSkeleton />}
            {!isLoading && isItemSelected && (
              <QuickSearchItemDetail
                focusedKey={focusedKey}
                onScroll={handleOnDetailScroll}
              />
            )}
          </ResultItemDetailContainer>
        </ContentGrid>
      )}
    </SearchResultsContainer>
  ) : (
    <QuickSearchInitial
      id={listState.id}
      onClickSearchSuggestion={handleOnClickSearchSuggestion}
      onScroll={handleOnInitialScroll}
      // @todo (SRCH-2005): Hardcode paddingTop={12} (tabbed-only)
      paddingTop={isTabbedListMode ? 12 : 0}
    />
  );

  /* ------------------------------------------------------------------
   * Flag OFF — original single-card layout — @todo (SRCH-2005): Remove
   * ------------------------------------------------------------------ */
  if (!isTabbedListMode) {
    return (
      <QuickSearchListAnimation layoutDependency={[noResults, hasSearchQuery]}>
        <QuickSearchListWrapper>
          <ClassicListSearchBox layout="preserve-aspect" layoutRoot={true}>
            {searchInput}
            {shouldShowSearchControls && (
              <SearchControlsRow>
                {shouldShowTabBar ? tabBar : null}
                {shouldShowFilterMenu ? (
                  <QuickSearchFilter
                    filters={filters}
                    isScopedToProject={isScopedToProject}
                    onClearAllFilters={onClearAllFilters}
                    onToggleFilter={onToggleFilter}
                  />
                ) : null}
              </SearchControlsRow>
            )}
          </ClassicListSearchBox>
          {resultsContent}
        </QuickSearchListWrapper>
      </QuickSearchListAnimation>
    );
  }

  /* ------------------------------------------------------------------
   * Flag ON — two-panel detached input layout
   * ------------------------------------------------------------------ */
  return (
    <QuickSearchShell $isMobile={isMobile}>
      <TabbedListSearchBox $isMobile={isMobile}>
        {searchInput}
      </TabbedListSearchBox>

      <QuickSearchListAnimation layoutDependency={[noResults, hasSearchQuery]}>
        <ResultsPanel $isMobile={isMobile}>
          {shouldShowTabBar && hasSearchQuery && (
            <ResultsPanelTabBar>{tabBar}</ResultsPanelTabBar>
          )}
          {hasSearchQuery && (
            <ResultsPanelFilterRow>
              <QuickSearchFilter
                filters={filters}
                isScopedToProject={isScopedToProject}
                onClearAllFilters={onClearAllFilters}
                onToggleFilter={onToggleFilter}
              />
            </ResultsPanelFilterRow>
          )}
          {resultsContent}
        </ResultsPanel>
      </QuickSearchListAnimation>
    </QuickSearchShell>
  );
});

QuickSearchList.displayName = 'QuickSearchList';

/* --------------------------------------------------------------------
 * Flag OFF — original single-card styled components — @todo (SRCH-2005): Remove
 * -------------------------------------------------------------------- */

const QuickSearchListWrapper = styled.div`
  background-color: ${theme.colors.background.panel};
  border-color: ${theme.colors.border.secondary};
  border-radius: ${theme.radii[8]};
  border-style: solid;
  border-width: 1px;
  contain: inline-size;
  display: flex;
  flex-direction: column;
  justify-self: center;
  max-height: 708px;
  overflow: hidden;
  transition-duration: ${theme.transitions.durations['100']};
  transition-property: width, max-width;
  transition-timing-function: ${theme.transitions.eases.fastStart};
  width: 100%;
  will-change: width, max-width;

  @media (min-width: ${theme.breakpoints.tablet}) {
    width: calc(100vw - ${DIALOG_GUTTER_WITH_BORDER}px);
    max-width: ${MODAL_MAX_WIDTH}px;
  }
`;

const ClassicListSearchBox = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spaces[16]};
  padding: ${theme.spaces[16]};
  position: relative;
`;

/* ====================================================================
 * Flag ON — two-panel detached input styled components
 * ==================================================================== */

const QuickSearchShell = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spaces[12]};
  justify-self: center;
  outline: none;
  width: 100%;

  @media (min-width: ${theme.breakpoints.tablet}) {
    width: calc(100vw - ${DIALOG_GUTTER_WITH_BORDER}px);
    max-width: ${MODAL_MAX_WIDTH}px;
  }
`;

const ResultsPanel = styled.div<{ $isMobile?: boolean }>`
  background-color: ${theme.colors.background.panel};
  border-color: ${theme.colors.border.secondary};
  border-radius: ${theme.radii[12]};
  border-style: solid;
  border-width: 1px;
  box-shadow: ${({ $isMobile }) => ($isMobile ? 'none' : theme.shadows['4dp'])};
  contain: inline-size;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
`;

const ResultsPanelTabBar = styled.div`
  flex-shrink: 0;
  padding-block: ${theme.spaces[12]} ${theme.spaces[8]};
  padding-inline: ${theme.spaces[16]};
`;

const ResultsPanelFilterRow = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  padding-block: var(--spaces-8);
  padding-block-start: 0;
  padding-inline: ${theme.spaces[16]};
`;

/* [class*='InputContainer'] and [class*='StyledInputAdornment'] target Vapor internals
 * by class-name substring — these will silently break if Vapor renames those classes.
 */
const TabbedListSearchBox = styled.div<{ $isMobile?: boolean }>`
  --colors-components-input-background: ${theme.colors.background.panel};
  display: flex;
  flex-direction: column;

  /* Override pill variant's border-radius and padding on all internal input wrappers */
  input,
  [role='searchbox'],
  [class*='InputContainer'] {
    border-radius: ${theme.radii[12]};
    padding-block: ${theme.spaces[6]};
    padding-inline-end: ${theme.spaces[8]};
  }

  /* Bump the search icon left margin from 8 → 16 */
  [class*='StyledInputAdornment'] {
    margin-left: ${theme.spaces[16]};
  }

  gap: ${theme.spaces[12]};
  outline: none;
  padding: 0;
  position: relative;
  width: 100%;

  @media (min-width: ${theme.breakpoints.tablet}) {
    gap: ${theme.spaces[16]};
  }
`;

/* ====================================================================
 * Shared styled components
 * ==================================================================== */

/** Container for search results that fills parent width and auto-adjusts height */
const SearchResultsContainer = styled.div`
  height: auto;
  width: 100%;
`;

const SearchControlsRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${theme.spaces[16]};
  width: 100%;
`;

const NoResultsContainer = styled.div`
  align-items: flex-start;
  display: flex;
  height: ${MODAL_CONTENT_HEIGHT}px;
  justify-content: center;
  padding-top: 104px;
  width: 100%;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-column-gap: 0px;
  grid-row-gap: 0px;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  height: ${MODAL_CONTENT_HEIGHT}px;
  overflow: hidden;

  @media (min-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: minMax(420px, ${CONTENT_LIST_MAX_WIDTH}px) ${CONTENT_DETAIL_PANE_WIDTH}px;
  }
`;

const ResultItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: ${MODAL_CONTENT_HEIGHT}px;
  min-height: 0;
  overflow: hidden;
`;

const ListBoxScrollArea = styled.div`
  --colors-components-list-box-header-shadow: none;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const ResultItemDetailContainer = styled.div`
  height: ${MODAL_CONTENT_HEIGHT}px;
  overflow: hidden;
`;

const TabbedResultsFooterRow = styled.div`
  align-items: center;
  background-color: ${theme.colors.background.panel};
  border-bottom-width: 0;
  border-image-slice: 1;
  border-image-source: linear-gradient(
    to right,
    transparent 0%,
    ${theme.colors.border.secondary} 50%,
    transparent 100%
  );
  border-left-width: 0;
  border-right-width: 0;
  border-top-style: solid;
  border-top-width: 1px;
  display: flex;
  gap: ${theme.spaces[4]};
  justify-content: flex-start;
  min-height: ${theme.spaces[48]};
  padding: ${theme.spaces[8]} ${theme.spaces[16]};
`;

const TabbedResultsFooterDivider = styled.div`
  align-self: stretch;
  display: flex;
  flex-shrink: 0;
  padding-block: ${theme.spaces[8]};

  &::before {
    border-left: 1px solid ${theme.colors.border.secondary};
    content: '';
  }
`;
